import { styled } from '@flex-design-system/stitches-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Primitive } from '@radix-ui/react-primitive';
import { fadeAnimation, nothing, slideAnimation, scaleAnimation } from '../../utils/keyframes.mjs';
import { PrimitiveButton } from '../button/primitive-button/PrimitiveButton.mjs';
import { ScrollArea } from '../scroll-area/ScrollArea.mjs';

const OUT_OFFSET = 20;
const PADDING_X = 40;
const FOG_HEIGHT = 80;
const calcMaxHeight = (offset = 0) => `calc(100vh - 56px + ${typeof offset === "string" ? offset : `${offset}px`})`;
const parseStyleValue = (value) => typeof value === "string" ? value : `${value}px`;
function getAdjustModalHeight(height, options) {
  const offsetHeight = options?.offset ?? 0;
  return `min(${parseStyleValue(height)}, ${calcMaxHeight(
    parseStyleValue(offsetHeight)
  )})`;
}
const StyledFog = styled(ScrollArea.Fog, {
  height: FOG_HEIGHT
});
const StyledOverlay = styled(DialogPrimitive.Overlay, {});
const StyledContentPositioner = styled(Primitive.div, {
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  position: "fixed",
  display: "flex",
  overflowY: "auto",
  // Overlay와 같은 효과를 줍니다.
  // Content가 애니메이션 되는 시간보다 길어야 합니다.
  // Positioner가 Content의 부모요소이기 때문에 부모가 먼저 닫혀버리면 Content의 closed 애니메이션이 끊어집니다.
  '&[data-state="closed"]': {
    animationName: nothing,
    animationDuration: fadeAnimation.duration.open
  },
  variants: {
    open: {
      false: {
        // 애니메이션이 끝난 후에 닫힐 수 있도록 Positioner에도 animation 할당
        animationName: nothing.name,
        animationDuration: fadeAnimation.duration.close
      }
    },
    modal: {
      true: {
        // Positioner는 Overlay와 함께 쓰이는 경우 포지션만 잡아줄 뿐 다른 역할을 가지면 안되므로 pointerEvents를 받지 않도록 한다.
        pointerEvents: "none !important"
      },
      false: {}
    },
    placement: {
      center: {
        justifyContent: "center",
        alignItems: "center"
      },
      top: {
        justifyContent: "center",
        alignItems: "flex-start"
      },
      bottom: {
        justifyContent: "center",
        alignItems: "flex-end"
      },
      left: {
        justifyContent: "flex-start",
        alignItems: "center"
      },
      right: {
        justifyContent: "flex-end",
        alignItems: "center"
      }
    }
  }
});
const StyledContent = styled(DialogPrimitive.Content, {
  maxHeight: calcMaxHeight(0),
  overflow: "hidden",
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: "$modal",
  display: "flex",
  position: "fixed",
  willChange: "transform, opacity",
  animationDuration: scaleAnimation.duration.open,
  animationTimingFunction: scaleAnimation.timingFunction,
  variants: {
    animation: {
      default: {
        "@motion": {
          '&[data-state="open"]': {
            animationName: scaleAnimation.show(0.98)
          },
          '&[data-state="closed"]': {
            animationName: scaleAnimation.hide(0.99)
          }
        }
      },
      slideFromBottom: {
        "@motion": {
          '&[data-state="open"]': {
            animationName: slideAnimation.show("0", "10%")
          },
          '&[data-state="closed"]': {
            animationName: slideAnimation.hide("0", "10%")
          }
        }
      },
      slideFromRight: {
        "@motion": {
          '&[data-state="open"]': {
            animationName: slideAnimation.show("10%", "0")
          },
          '&[data-state="closed"]': {
            animationName: slideAnimation.hide("10%", "0")
          }
        }
      }
    }
  },
  "&:focus": { outline: "none" }
});
const ContentRoot = styled(Primitive.div, {
  width: "100%",
  maxHeight: "100%",
  minHeight: "100%",
  display: "flex",
  flexDirection: "column",
  variants: {
    hasAsideChild: {
      true: {
        flexDirection: "row"
      }
    }
  }
});
const StyledSection = styled(Primitive.div, {
  width: "100%",
  maxHeight: "100%",
  minHeight: "100%",
  display: "flex",
  flexDirection: "column"
});
const StyledToolbar = styled(Primitive.div, {
  width: "100%",
  padding: "18px 20px",
  backgroundColor: "$white",
  display: "flex",
  alignItems: "center",
  /**
   * position: sticky로 처리하면 높이계산에서 흔들리는 이슈가 있다.
   * https://bugs.chromium.org/p/chromium/issues/detail?id=734461
   * https://flex-cv82520.slack.com/archives/C02U6EBG9U1/p1654786456290799?thread_ts=1654781048.263049&cid=C02U6EBG9U1
   */
  position: "fixed",
  top: 0,
  // 스크롤이 일어날때 다른요소보다 위에 있게 하는 용도
  zIndex: 1,
  willChange: "transform"
});
const scrolledToolbarStyle = {
  backgroundColor: "rgba(252, 252, 252, 0.9)",
  borderBottom: "1px solid $blackAlpha004",
  // https://monorail-prod.appspot.com/p/chromium/issues/detail?id=978031
  backdropFilter: "blur(8px)"
};
const StyledHeader = styled("header", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  px: PADDING_X,
  paddingBottom: 24
});
const StyledTitle = styled(DialogPrimitive.Title, {
  width: "100%",
  color: "$grayDark",
  fontWeight: 700,
  fontSize: 28,
  letterSpacing: "-0.04em"
});
const StyledDescription = styled(DialogPrimitive.Description, {
  marginTop: 14,
  color: "$gray",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.4
});
const StyledScrollAreaRoot = styled(Primitive.div, {
  width: "100%",
  flexGrow: 1,
  overflow: "hidden",
  /**
   * ScrollArea div 의 height를 100% 로 주기 위함
   * 상위 (Modal.Content) 의 height 를 따라가기 위함
   */
  "& > div": {
    height: "100%"
  }
});
const StyledScrollAreaViewport = styled(ScrollArea.Viewport_Legacy, {
  minWidth: "520px",
  maxWidth: "600px",
  width: `calc(100vw - ${OUT_OFFSET * 2}px)`,
  display: "flex",
  flexDirection: "column",
  /**
   * 기존 처럼 height: 100% 로 주게 되면, 부모의 height 를 따라가서 스크롤이 Viewport 패딩 만큼 생기지 않는다.
   * height 를 unset 로 주어서 자식의 height 를 갖도록 수정한다.
   */
  "> div[style]": {
    height: "unset",
    flexGrow: 1
  }
});
const StyledScrollAreaChildrenRoot = styled("div", {
  display: "flex",
  flexDirection: "column",
  height: "100%"
});
const StyledFooter = styled("footer", {
  width: "100%",
  padding: `0 ${PADDING_X}px 44px`,
  flexShrink: 0
});
const StyledCloseIconButtonRoot = styled(PrimitiveButton, {
  display: "block",
  cursor: "pointer",
  marginLeft: "auto",
  px: 2,
  py: 2,
  "&&": {
    borderRadius: 6,
    lineHeight: 0,
    transition: "transform 0.1s",
    "&:before": {
      content: "unset"
    },
    path: {
      transition: "fill 0.1s"
    },
    "&:hover": {
      path: {
        fill: "$blackAlpha024"
      }
    },
    "&:active": {
      transform: "scale(0.94)",
      path: {
        transition: "fill 0s",
        fill: "$blackAlpha032"
      }
    }
  }
});
const StyledBody = styled(Primitive.div, {
  px: PADDING_X
});
const StyledPortalContainer = styled(Primitive.div, {
  position: "relative"
});

export { ContentRoot, FOG_HEIGHT, PADDING_X, StyledBody, StyledCloseIconButtonRoot, StyledContent, StyledContentPositioner, StyledDescription, StyledFog, StyledFooter, StyledHeader, StyledOverlay, StyledPortalContainer, StyledScrollAreaChildrenRoot, StyledScrollAreaRoot, StyledScrollAreaViewport, StyledSection, StyledTitle, StyledToolbar, getAdjustModalHeight, scrolledToolbarStyle };
//# sourceMappingURL=styled.mjs.map
